import React, { useState, useEffect } from "react";
import {
    Box, Paper, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Chip,
    CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, MenuItem, Select, FormControl,
    InputLabel, Tooltip, InputAdornment, TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import axiosInstance from "./Axiosinstance";

const STATUS_OPTIONS = ["NEW", "READ", "RESPONDED"];

const statusColor = (status) => {
    switch (status) {
        case "NEW":       return { bg: "rgba(25,118,210,0.1)",  text: "#1976d2" };
        case "READ":      return { bg: "rgba(245,124,0,0.1)",   text: "#f57c00" };
        case "RESPONDED": return { bg: "rgba(46,125,50,0.1)",   text: "#2e7d32" };
        default:          return { bg: "rgba(0,0,0,0.06)",      text: "#666" };
    }
};

const AdminContacts = () => {
    const [enquiries,    setEnquiries]    = useState([]);
    const [filtered,     setFiltered]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [search,       setSearch]       = useState("");
    const [viewItem,     setViewItem]     = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [success,      setSuccess]      = useState("");
    const [error,        setError]        = useState("");

    const fetchEnquiries = () => {
        setLoading(true);
        axiosInstance.get("/api/admin/contacts")
            .then((res) => {
                setEnquiries(res.data.data || []);
                setFiltered(res.data.data || []);
            })
            .catch(() => setError("Failed to load enquiries."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchEnquiries(); }, []);

    useEffect(() => {
        const q = search.toLowerCase();
        setFiltered(
            enquiries.filter(
                (e) =>
                    e.name.toLowerCase().includes(q) ||
                    e.email.toLowerCase().includes(q) ||
                    (e.subject || "").toLowerCase().includes(q)
            )
        );
    }, [search, enquiries]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            await axiosInstance.patch(`/api/admin/contacts/${id}/status?status=${newStatus}`);
            setSuccess("Status updated.");
            fetchEnquiries();
        } catch {
            setError("Failed to update status.");
        }
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/api/admin/contacts/${deleteDialog.id}`);
            setSuccess("Enquiry deleted.");
            setDeleteDialog({ open: false, id: null });
            fetchEnquiries();
        } catch {
            setError("Failed to delete.");
        }
    };

    const handleView = (item) => {
        setViewItem(item);
        if (item.status === "NEW") handleStatusChange(item.id, "READ");
    };

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Contact Enquiries</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filtered.length} enquir{filtered.length !== 1 ? "ies" : "y"} found
                    </Typography>
                </Box>
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}
            {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}

            <TextField
                fullWidth
                placeholder="Search by name, email, or subject..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" } }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                    ),
                }}
            />

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ background: "rgba(123,31,162,0.04)" }}>
                            {["#", "Name", "Email", "Phone", "Subject", "Date", "Actions"].map((h) => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "13px", color: "text.secondary", py: 1.75 }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5, color: "text.secondary" }}>
                                    No enquiries found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map((item, idx) => {
                                const sc = statusColor(item.status);
                                return (
                                    <TableRow key={item.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                        <TableCell sx={{ color: "text.secondary", fontSize: "13px" }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                                        <TableCell sx={{ fontSize: "13px", color: "text.secondary" }}>{item.email}</TableCell>
                                        <TableCell sx={{ fontSize: "13px" }}>{item.phone || "—"}</TableCell>
                                        <TableCell sx={{ fontSize: "13px" }}>{item.subject || "—"}</TableCell>
                                        {/* <TableCell>
                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                                <Select
                                                    value={item.status || "NEW"}
                                                    onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                                    sx={{
                                                        borderRadius: 2, fontSize: "12px", fontWeight: 600,
                                                        background: sc.bg, color: sc.text,
                                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                                                        "& .MuiSelect-icon": { color: sc.text },
                                                    }}
                                                >
                                                    {STATUS_OPTIONS.map((s) => (
                                                        <MenuItem key={s} value={s} sx={{ fontSize: "13px" }}>{s}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell> */}
                                        <TableCell sx={{ fontSize: "12px", color: "text.secondary", whiteSpace: "nowrap" }}>
                                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="View message">
                                                <IconButton size="small" onClick={() => handleView(item)} sx={{ color: "#7b1fa2", mr: 0.5 }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: item.id })} sx={{ color: "error.main" }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* View Dialog */}
            <Dialog open={Boolean(viewItem)} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Enquiry from {viewItem?.name}</DialogTitle>
                <DialogContent>
                    {viewItem && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[
                                ["Email",   viewItem.email],
                                ["Phone",   viewItem.phone || "—"],
                                ["Subject", viewItem.subject || "—"],
                            ].map(([label, value]) => (
                                <Box key={label}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
                                    <Typography variant="body2">{value}</Typography>
                                </Box>
                            ))}
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Message</Typography>
                                <Paper elevation={0} sx={{ mt: 0.5, p: 2, background: "rgba(0,0,0,0.03)", borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{viewItem.message}</Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Received</Typography>
                                <Typography variant="body2">
                                    {viewItem.createdAt ? new Date(viewItem.createdAt).toLocaleString() : "—"}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setViewItem(null)} sx={{ textTransform: "none", borderRadius: 2 }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete Enquiry?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, id: null })} sx={{ textTransform: "none", borderRadius: 2 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminContacts;
